/**
 * Résolution d'un chemin d'annuaire — la DÉCISION, sans aucune I/O.
 *
 * Le front est seul à décider du code HTTP servi sur `/annuaire-velo/…` ;
 * l'api détient les faits (FIX-542, décision 9). L'ordre est fixe :
 *
 *   1. fiche vivante            → 200
 *   2. sinon Redirection connue → son code et sa destination (301 ou 410)
 *   3. sinon                    → 404  (aujourd'hui encore filtré par
 *                                       FALLBACK_TO_CITY_PAGE, voir plus bas)
 *
 * Ce module est volontairement PUR et ISOMORPHE : aucune dépendance Node, aucun
 * réseau. Deux raisons.
 *   - Testabilité : `src/server/` n'a aucun test ; en sortant la décision de
 *     l'Express, elle se couvre exhaustivement en Jest sans monter de serveur.
 *   - Cohérence client/serveur : `src/index.jsx` fait un `render()` et non un
 *     `hydrate()`, donc le client réécrit intégralement ce que le SSR a produit.
 *     Si le miroir client appliquait une autre règle, une page 404/410
 *     correctement servie serait remplacée par une redirection côté navigateur —
 *     l'utilisateur verrait le contraire de ce que voit Google. Le SSR
 *     (`src/server/index.jsx`) et les miroirs client (`WorkshopStandalone`,
 *     `WorkshopRedirect`) importent donc la MÊME fonction.
 */

export const ANNUAIRE_ROOT = '/annuaire-velo';

/**
 * Filet historique de la migration de mars 2026 : quand aucune Redirection n'est
 * connue pour un chemin d'annuaire absent, on 301 vers la page ville. C'est le
 * comportement servi en production aujourd'hui — appliqué sans distinction, y
 * compris à un chemin qui n'a jamais existé.
 *
 * `false` ⇒ un chemin d'annuaire inconnu répond un vrai 404.
 *
 * NE PAS passer à `false` dans cette PR. Le prérequis est une PR d'une ligne,
 * bloquée sur DEUX conditions côté api (FIX-542, décision 3 ; ADR api 0022) :
 *   1. `GET /annuaire/resolve` déployé en production ET backfill des 12 chemins
 *      morts exécuté ;
 *   2. vérification au curl 12/12 — chacun des 12 chemins renvoie le code et la
 *      destination attendus.
 * Sans ce prérequis, la bascule transforme en 404 sec des URL historiquement
 * indexées (dont les deux chemins `educator-specialized-*` nommés par le ticket,
 * qui 301 aujourd'hui). C'est le seul geste irréversible du lot ; tant que la
 * constante vaut `true`, cette PR est mergeable avant OU après l'api.
 *
 * Rollback : remettre `true`, redéployer.
 */
export const FALLBACK_TO_CITY_PAGE = true;

/**
 * `url_path` d'une fiche (`aude/villepinte/xxx`) → chemin absolu depuis la
 * racine du site, seul espace d'adressage de la table de Redirections
 * (décision 2). Renvoie `null` sur une entrée vide plutôt qu'un chemin bancal.
 */
export const annuairePathFromUrlPath = (urlPath) => {
  if (!urlPath) return null;
  const trimmed = String(urlPath).replace(/^\/+/, '').replace(/\/+$/, '');
  return trimmed ? `${ANNUAIRE_ROOT}/${trimmed}` : null;
};

/**
 * Reporte la query de l'adresse demandée sur la destination d'une redirection
 * de fiche : `?via=` du lien ou du QR du réparateur (FIXERLIGHT-062), les
 * `utm_*`, tout paramètre. Un QR imprimé avant le renommage d'une fiche doit
 * arriver sur la nouvelle adresse avec son marqueur. La query est reprise telle
 * quelle, déjà encodée ; elle se pose avant un éventuel fragment et s'ajoute à
 * celle que la destination porterait déjà. Le canonical, lui, reste le chemin
 * seul (`src/server/index.jsx`).
 */
export function garderQuery(location, search) {
  const query = String(search || '').replace(/^\?/, '');
  if (!query) return location;
  const diese = location.indexOf('#');
  const avant = diese === -1 ? location : location.slice(0, diese);
  const fragment = diese === -1 ? '' : location.slice(diese);
  const lien = avant.includes('?') ? '&' : '?';
  return `${avant}${lien}${query}${fragment}`;
}

/**
 * Écarte une Redirection inexploitable plutôt que de la servir. L'api aplatit
 * les chaînes à l'écriture (ADR 0022) et le front n'en déroule aucune : une
 * destination absente ou égale au chemin demandé est un bug de données, pas une
 * chaîne à suivre. On journalise et on retombe sur le palier suivant.
 */
const usableRedirect = (redirect, requestPath) => {
  if (!redirect) return null;
  // Le motif est journalisé partout : sans lui, un 410 `FICHE_ERRONEE` et un 410
  // `MANUEL` sont indiscernables dans les logs de prod.
  const tag = `${requestPath} (reason=${redirect.reason || 'inconnu'})`;

  if (redirect.httpCode === 410) return redirect;
  if (redirect.httpCode !== 301) {
    console.error(
      `[annuaire] code de Redirection non supporté (${redirect.httpCode}) pour ${tag}`,
    );
    return null;
  }
  if (!redirect.destination) {
    console.error(`[annuaire] Redirection 301 sans destination pour ${tag}`);
    return null;
  }
  // Le contrat impose un chemin absolu depuis la racine du site (décision 2).
  // Une destination relative se résoudrait contre l'url courante — mauvaise
  // cible ; une destination `https://…` enverrait le trafic hors du site. Ni
  // l'une ni l'autre n'est servie.
  if (!redirect.destination.startsWith('/')) {
    console.error(
      `[annuaire] destination non absolue (${redirect.destination}), ignorée pour ${tag}`,
    );
    return null;
  }
  if (requestPath && redirect.destination === requestPath) {
    console.error(`[annuaire] Redirection pointant sur elle-même : ${tag}`);
    return null;
  }
  return redirect;
};

/**
 * @param {Object}   input
 * @param {'found'|'missing'|'error'} input.shopStatus  réponse de `/shops/by-path`
 * @param {?Object}  input.redirect          Redirection normalisée, cf. redirectApi.js
 * @param {boolean}  input.redirectLookupFailed  le lookup a jeté
 * @param {?string}  input.fallbackLocation  page ville, ou `null` si le filet est coupé
 * @param {?string}  input.requestPath       chemin demandé, pour la détection d'auto-boucle
 * @returns {{type:'render'}
 *          |{type:'redirect', status:301, location:string}
 *          |{type:'gone'}
 *          |{type:'notFound'}
 *          |{type:'unavailable'}}
 */
export function decideAnnuaireResponse({
  shopStatus,
  redirect = null,
  redirectLookupFailed = false,
  fallbackLocation = null,
  requestPath = null,
}) {
  if (shopStatus === 'found') return { type: 'render' };

  // L'api est tombée : on ne sait pas si la fiche est vivante. Un 404 servi par
  // erreur pendant un incident coûterait les 24 % de trafic de l'annuaire ;
  // Google réessaie un 503 et ne désindexe pas (décision 15).
  if (shopStatus === 'error') return { type: 'unavailable' };

  const usable = usableRedirect(redirect, requestPath);
  if (usable) {
    if (usable.httpCode === 410) return { type: 'gone' };
    return { type: 'redirect', status: 301, location: usable.destination };
  }

  // Le lookup a échoué (endpoint absent, api en panne, JSON illisible). Tant que
  // le filet est en place, on sert exactement ce que la prod sert aujourd'hui —
  // c'est ce qui rend cette PR mergeable avant l'api. Filet coupé, on ne peut
  // plus distinguer « chemin mort » de « jamais existé » : 503, jamais 404.
  if (redirectLookupFailed) {
    if (fallbackLocation && fallbackLocation !== requestPath) {
      return { type: 'redirect', status: 301, location: fallbackLocation };
    }
    return { type: 'unavailable' };
  }

  if (fallbackLocation && fallbackLocation !== requestPath) {
    return { type: 'redirect', status: 301, location: fallbackLocation };
  }

  return { type: 'notFound' };
}
